import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Clock, UserX, AlertCircle } from 'lucide-react';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { BoardInvitation } from '../types';
import { getPendingInvitationsForBoard, cancelInvitation } from '../firebase/firestore';

interface PendingInvitationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  boardId: string;
  boardName: string;
}

const PendingInvitationsModal: React.FC<PendingInvitationsModalProps> = ({
  isOpen,
  onClose,
  boardId,
  boardName,
}) => {
  const [pendingInvitations, setPendingInvitations] = useState<BoardInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPendingInvitations = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const invitations = await getPendingInvitationsForBoard(boardId);
      setPendingInvitations(invitations);
    } catch (error) {
      console.error('Error loading pending invitations:', error);
      setError('Failed to load pending invitations');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && boardId) {
      loadPendingInvitations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadPendingInvitations is stable
  }, [isOpen, boardId]);

  // Add escape key functionality
  useEscapeKey(onClose, isOpen);

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      await cancelInvitation(invitationId);
      // Reload the list
      await loadPendingInvitations();
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      setError('Failed to cancel invitation');
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'editor':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'viewer':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getTimeRemaining = (expiresAt: Date) => {
    const now = new Date();
    const diffInHours = Math.floor((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Expires soon';
    if (diffInHours < 24) return `${diffInHours}h remaining`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d remaining`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-white/15 backdrop-blur-xl z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="glass-effect rounded-2xl p-6 w-full max-w-md max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Pending Invitations</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                <AlertCircle size={16} className="text-red-500" />
                <span className="text-sm text-red-700">{error}</span>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-4 border-satsuma-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-gray-500">Loading pending invitations...</p>
              </div>
            )}

            {/* Invitations List */}
            {!isLoading && (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {pendingInvitations.length === 0 ? (
                  <div className="text-center py-8">
                    <Mail size={48} className="text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No pending invitations</p>
                    <p className="text-xs text-gray-400 mt-1">All invitations have been accepted or expired</p>
                  </div>
                ) : (
                  pendingInvitations.map((invitation) => (
                    <motion.div
                      key={invitation.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="glass-effect rounded-xl p-4 border border-white/30"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-800 mb-1">
                            {invitation.email}
                          </h3>
                          <div className="flex items-center space-x-2 mb-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getRoleColor(invitation.role)}`}>
                              {invitation.role}
                            </span>
                            <div className="flex items-center space-x-1 text-xs text-gray-500">
                              <Clock size={12} />
                              <span>{formatTimeAgo(invitation.createdAt)}</span>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500">
                            {getTimeRemaining(invitation.expiresAt)}
                          </p>
                        </div>
                      </div>

                      {/* Cancel Button */}
                      <button
                        onClick={() => handleCancelInvitation(invitation.id)}
                        className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                      >
                        <UserX size={16} />
                        <span className="text-sm font-medium">Cancel Invitation</span>
                      </button>
                    </motion.div>
                  ))
                )}
              </div>
            )}

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-white/30">
              <p className="text-xs text-gray-500 text-center">
                Invitations expire in 7 days. Users will be notified when they log in.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PendingInvitationsModal;
